import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLangContext } from 'components/shared/Lang';
import { Col, Row, Spin } from 'antd';
import css from '../../css/card.module.css';
import styles from 'components/ant/css/stylesIndex.module.css';
import { useAuthContext } from 'components/shared/Auth';
import CardFooter from 'components/views/card/footer';
import ImageGallery from 'react-image-gallery';
import CardHeader from 'components/views/card/header';
import { getImageVersionUrl } from 'lib/images';
import Slide from './Slide';
import ChatMessage from './ChatMessage.js';
import CommentForm from './CommentForm.js';
import ResilientImage from 'components/shared/media/ResilientImage';
import ResilientVideo from 'components/shared/media/ResilientVideo';
import Link from '../../../shared/elements/Link';
import classNames from 'classnames';
import { EditorState } from 'draft-js';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
// import { w3cwebsocket as W3CWebSocket } from 'websocket';
// const client = new W3CWebSocket('wss://wss.ruex.eu/');

export const Card = ({ item }) => {
  const { t } = useLangContext();
  const { data, isAuth } = useAuthContext();
  const [messages, setMessages] = useState([]);
  const [usersById, setUsersById] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastComment, setLastComment] = useState(null);
  const [chatRoomUuid, setChatRoomUuid] = useState(item.chat_room_uuid || null);
  const pendingUserIdsRef = useRef({});

  const messageRef = useRef();

  const tags = String(item.description).match(/(^|\s)(#[a-zа-я\d-]+)/gi);
  const tagsA = String(item.description).match(/(^|\s)(@[a-zа-я\d-]+)/gi);
  let text = String(item.description).replace(/(^|\s)(#[a-zа-я\d-]+)/gi, '');
  text = text.replace(/(^|\s)(@[a-zа-я\d-]+)/gi, '');

  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const onEditorStateChange = function (editorState) {
    setEditorState(editorState);
  };

  const getEditorMessageText = (state) => {
    if (!state) return '';
    return state.getCurrentContent().getPlainText('\n').trim();
  };

  // client.onopen = () => {
  //   setWsConnected(true);
  // };

  // client.onmessage = (e) => {
  //   getComments();
  //   let data;
  //   try {
  //     data = JSON.parse(e?.data ?? '');
  //     if (data?.message) {
  //     }
  //   } catch (error) {}
  // };

  // client.onerror = function () {};

  // useEffect(() => {
  //   console.log('wsConnected', wsConnected);
  //   try {
  //     wsConnected && client?.send(JSON.stringify({ method: 'followChannel', channel: item.id }));
  //   } catch (e) {}
  // }, [wsConnected]);

  const getUserId = useCallback((user) => {
    if (!user) return null;
    return user?.id ?? user?.user_id ?? user?.uuid ?? user?.user_uuid ?? null;
  }, []);

  const getMessageUserId = useCallback((messageItem) => {
    if (!messageItem) return null;
    return messageItem?.user_id ?? getUserId(messageItem?.user);
  }, [getUserId]);

  const getUserName = useCallback((user) => {
    if (!user) return '...';
    const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
    return fullName || user?.name || user?.userName || user?.username || '...';
  }, []);

  const mergeUsers = useCallback((incomingUsers = []) => {
    if (!Array.isArray(incomingUsers) || incomingUsers.length === 0) return;

    setUsersById((prev) => {
      const next = { ...prev };
      incomingUsers.forEach((incomingUser) => {
        const id = getUserId(incomingUser);
        if (!id) return;
        next[id] = { ...(next[id] || {}), ...incomingUser, id };
      });
      return next;
    });
  }, [getUserId]);

  const ensureChatRoom = useCallback(() => {
    if (chatRoomUuid) return Promise.resolve(chatRoomUuid);
    return fetch(`/api/listings/${item.id}/chat-room`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
      .then((response) => response.json())
      .then((data) => {
        const uuid = data?.data?.chat_room_uuid;
        if (uuid) setChatRoomUuid(uuid);
        return uuid;
      });
  }, [chatRoomUuid, item.id]);

  const getComments = useCallback(() => {
    setLoaded(true);
    const fetchComments = (uuid) => {
      const url = uuid
        ? `/api/chat-rooms/${uuid}/comments`
        : `/api/comments/${item.id}`;
      fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      })
        .then((response) => response.json())
        .then((data) => {
          mergeUsers(data?.users);
          const normalizedMessages = Array.isArray(data?.data)
            ? data.data.map((messageItem) => ({
                ...messageItem,
                user_id: getMessageUserId(messageItem),
              }))
            : [];
          setMessages(normalizedMessages);
          setLoaded(false);
          messageRef.current?.scroll({
            top: 0,
            behavior: 'smooth',
          });
        })
        .catch(() => {
          setLoaded(false);
        });
    };

    if (chatRoomUuid) {
      fetchComments(chatRoomUuid);
    } else if (isAuth) {
      ensureChatRoom().then(fetchComments);
    } else {
      fetchComments(null);
    }
  }, [chatRoomUuid, ensureChatRoom, getMessageUserId, isAuth, item.id, mergeUsers]);

  useEffect(() => {
    if (!Array.isArray(messages) || messages.length === 0) return;

    const missingUserIds = [...new Set(messages.map((messageItem) => getMessageUserId(messageItem)).filter(Boolean))].filter(
      (id) => !usersById[id] && !pendingUserIdsRef.current[id],
    );

    if (missingUserIds.length === 0) return;

    missingUserIds.forEach((id) => {
      pendingUserIdsRef.current[id] = true;
    });

    Promise.all(
      missingUserIds.map((id) =>
        fetch(`/api/user/${id}`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        })
          .then((response) => (response.ok ? response.json() : null))
          .then((payload) => ({ id, user: payload }))
          .catch(() => ({ id, user: null })),
      ),
    )
      .then((resolvedUsers) => {
        const nextUsers = [];
        resolvedUsers.forEach(({ user, id }) => {
          if (!user) return;
          if (Array.isArray(user?.data)) {
            nextUsers.push(...user.data);
            return;
          }
          nextUsers.push(user?.data || user);
          if (!getUserId(user?.data || user)) {
            nextUsers.push({ ...(user?.data || user), id });
          }
        });
        mergeUsers(nextUsers);
      })
      .finally(() => {
        missingUserIds.forEach((id) => {
          delete pendingUserIdsRef.current[id];
        });
      });
  }, [getMessageUserId, getUserId, mergeUsers, messages, usersById]);
  const putComment = () => {
    const messageText = getEditorMessageText(editorState);
    if (!messageText || isSubmitting) return;

    const sendComment = (uuid) => {
      const body = uuid
        ? { chatRoomUUid: uuid, parent: null, comment: messageText }
        : { listing_id: item.id, parent: null, comment: messageText };

      return fetch(`/api/comments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
        .then((response) => response.json())
        .then((data) => {
          setLastComment(data?.id);
          setTimeout(() => {
            setLastComment(null);
          }, 2000);
          setEditorState(EditorState.createEmpty());
          return getComments();
        });
    };

    setIsSubmitting(true);
    const request = chatRoomUuid ? sendComment(chatRoomUuid) : ensureChatRoom().then(sendComment);
    request.catch(() => {}).finally(() => setIsSubmitting(false));
  };

  useEffect(() => {
    setChatRoomUuid(item.chat_room_uuid || null);
  }, [item?.chat_room_uuid, item?.id]);

  useEffect(() => {
    getComments();
  }, [getComments]);

  function _renderLegacyIframeVideo(mediaItem) {
    const aspectRatio = mediaItem?.width && mediaItem?.height ? `${mediaItem.width} / ${mediaItem.height}` : '16 / 9';

    return (
      <div className={css.videoFrameWrapper}>
        <iframe
          className={css.videoIframe}
          style={{ aspectRatio }}
          src={mediaItem.embedUrl}
          frameBorder='0'
          loading='lazy'
          allowFullScreen
        ></iframe>
      </div>
    );
  }

  function getVideoSource(mediaItem) {
    const variants = mediaItem?.metadata?.variants;
    const backupVariants = mediaItem?.metadata?.backup_variants;
    if (variants && typeof variants === 'object') {
      return {
        primary: variants['720'] || variants['1080'] || variants['360'] || Object.values(variants).find(Boolean) || mediaItem?.url || null,
        backup:
          (backupVariants && (backupVariants['720'] || backupVariants['1080'] || backupVariants['360'] || Object.values(backupVariants).find(Boolean))) ||
          mediaItem?.backup_url ||
          null,
      };
    }
    return {
      primary: mediaItem?.url || null,
      backup: mediaItem?.backup_url || null,
    };
  }

  function _renderNativeVideo(mediaItem) {
    const source = getVideoSource(mediaItem);
    if (!source?.primary && !source?.backup) return null;

    return (
      <div className={css.videoFrameWrapper}>
        <ResilientVideo
          className={css.videoPlayer}
          src={source.primary}
          backupSrc={source.backup}
          preferenceKey={`listing-video:${mediaItem?.id || mediaItem?.url || 'unknown'}`}
          controls
          preload='metadata'
          playsInline
          poster={mediaItem?.url_tiny || mediaItem?.backup_url_tiny || mediaItem?.thumbnail || undefined}
        />
      </div>
    );
  }

  const covers = (item?.covers || []).map((value) => value.id);
  const mediaFromItem = Array.isArray(item?.media) ? item.media : [];
  const imageMediaFromItem = mediaFromItem.filter((mediaItem) => mediaItem?.type !== 'video');
  const videoMediaFromItem = mediaFromItem.filter((mediaItem) => mediaItem?.type === 'video');

  const images = imageMediaFromItem.length > 0 ? imageMediaFromItem :
    (Array.isArray(covers) &&
      covers.map((value) => ({
        original: getImageVersionUrl({ id: value, version: 'cover' }),
        thumbnail: getImageVersionUrl({ id: value, version: 'tiny' }),
      }))) ||
    [];
  const videosFromMedia =
    (Array.isArray(videoMediaFromItem) &&
      videoMediaFromItem.map((videoItem) => ({
        ...videoItem,
        original: videoItem?.url_tiny || videoItem?.thumbnail || videoItem?.url,
        backup_original: videoItem?.backup_url_tiny || videoItem?.backup_url || null,
        thumbnail: videoItem?.url_tiny || videoItem?.thumbnail || videoItem?.url,
        renderItem: _renderNativeVideo.bind(this),
      }))) ||
    [];
  const legacyVideos =
    (Array.isArray(item?.videos) &&
      item?.videos.map(({ id, width, height }) => ({
        embedUrl: `https://customer-ja6fhk6t3y92qtj8.cloudflarestream.com/${id}/iframe`,
        width,
        height,
        renderItem: _renderLegacyIframeVideo.bind(this),
      }))) ||
    [];
  const videos = [...videosFromMedia, ...legacyVideos];
  const mediaItems = [...images, ...videos];
  const hasSingleImage = images.length === 1 && videos.length === 0;
  const shouldShowGallery = !hasSingleImage && mediaItems.length > 0;

  return (
    <Row className={css.content}>
      <Row className={classNames(css.row, css.mainRow)}>
        <Col className={css.card}>
          <div className={css.cardBody}>
            <CardHeader item={item} isVip single={true} />
            <div className={css.cardImg}>
              {hasSingleImage && (
                <div>
                  <ResilientImage
                    src={images[0].original || images[0].url_tiny || images[0].url}
                    backupSrc={images[0].backup_original || images[0].backup_url_tiny || images[0].backup_url}
                    alt=''
                    preferenceKey={`listing-image:${images[0].id || item?.id || 'single'}`}
                    style={{ width: '100%' }}
                  />
                </div>
              )}
              {shouldShowGallery && (
                <ImageGallery
                  items={mediaItems}
                  lazyLoad={true}
                  loading='lazy'
                  thumbnailLoading='lazy'
                  renderItem={Slide}
                  showNav={false}
                  showThumbnails={false}
                  showBullets
                  showPlayButton={false}
                  showFullscreenButton={false}
                  useBrowserFullscreen={false}
                />
              )}
            </div>
          </div>
        </Col>
        <Col className={classNames(css.priceBox, css.rightColumn)} style={{ minHeight: '40px' }}>
          <div className={css.priceBody}>
            <div className={css.cardTitle}>{item.title}</div>
            {item.price != 0 && item.price != '0.00' && (
              <div className={css.price}>
                {item.price} {item.currency}
              </div>
            )}
          </div>
          <div className={styles.cardBox}>
            <div
              className={[css.body, css.cardVipFooter].join(' ')}
              style={{
                paddingTop: '6px',
              }}
            >
              <CardFooter item={item} />
            </div>
          </div>
          <div className={classNames(styles.cardBox, css.detailsDescription)}>
            <div className={classNames(css.body, styles.cardBody, styles.cardDiv)}>
              <h4 className={styles.cardH4}>{t('Sellers Description')}</h4>
              {text}

              {tags && (
                <div className={classNames(css.tag, styles.cardTag)}>
                  {tags.map((item, i) => {
                    return (
                      <Link key={i} className={styles.cardLinkKey} href={`/search/?t=${encodeURIComponent(item)}`}>
                        {item}
                      </Link>
                    );
                  })}
                </div>
              )}
              {tagsA && (
                <div className={classNames(css.tag, styles.cardTag)}>
                  {tagsA.map((item, i) => {
                    return (
                      <Link key={i} className={styles.cardLinkKey} href={`/search/?t=${encodeURIComponent(item)}`}>
                        {item}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <div className={classNames(styles.cardBox, styles.cardColBox, css.detailsChat)}>
            <div className={classNames(css.bodyChat, styles.cardBodyChat)}>
              {!isAuth &&
                messages &&
                messages.map((item, index) => {
                  if (index < 5 && !item?.t) {
                    return (
                      <ChatMessage
                        key={item?.id ?? `${item?.created_at}_${index}`}
                        messageItem={item}
                        usersById={usersById}
                        getMessageUserId={getMessageUserId}
                        getUserName={getUserName}
                        currentUserId={data?.id}
                        lastComment={lastComment}
                        previewMode
                      />
                    );
                  }
                })}
              {isAuth && (
                <div
                  ref={messageRef}
                  style={{
                    maxHeight: '300px',
                    overflowY: 'scroll',
                  }}
                >
                  {loaded && <Spin style={{ position: 'absolute', top: '10px', left: '20px', zIndex: '20' }} />}
                  {messages &&
                    messages.map((item, index) => {
                      return (
                        <ChatMessage
                          key={item?.id ?? `${item?.created_at}_${index}`}
                          messageItem={item}
                          usersById={usersById}
                          getMessageUserId={getMessageUserId}
                          getUserName={getUserName}
                          currentUserId={data?.id}
                          lastComment={lastComment}
                        />
                      );
                    })}
                </div>
              )}

              {isAuth && (
                <CommentForm
                  editorState={editorState}
                  onEditorStateChange={onEditorStateChange}
                  onSubmit={putComment}
                  isSubmitting={isSubmitting}
                  isSubmitDisabled={!getEditorMessageText(editorState)}
                  formStyle={{ margin: '8px 10px 0 10px' }}
                  editorStyle={{
                    border: '1px solid #ddd',
                    padding: '0px 8px',
                    borderRadius: '0 0 6px 6px',
                    borderTop: 'none',
                    maxHeight: '80px',
                    marginBottom: '10px',
                  }}
                  buttonStyle={{
                    position: 'absolute',
                    bottom: '18px',
                    right: '12px',
                    zIndex: '50',
                    width: '50px',
                    borderRadius: '10px',
                    padding: '4px 2px',
                  }}
                />
              )}
            </div>
          </div>
        </Col>
      </Row>
    </Row>
  );
};
